import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { noticiasService } from '../services/noticias'
import type { Noticia, NoticiaFormData, NoticiaFiltros } from '../types/noticias'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { Plus, Search, Edit, Trash2, Star, StarOff, Calendar, User, Clock, FileText } from 'lucide-react'

const NoticiasPage = () => {
  const { user } = useAuth()
  const [noticias, setNoticias] = useState<Noticia[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingNoticia, setEditingNoticia] = useState<Noticia | null>(null)
  const [filtros, setFiltros] = useState<NoticiaFiltros>({ status: 'all' })
  const [paginaAtual, setPaginaAtual] = useState(1)
  const [totalPaginas, setTotalPaginas] = useState(1)
  const [total, setTotal] = useState(0)

  // Form state
  const [formData, setFormData] = useState<NoticiaFormData>({
    titulo: '',
    conteudo: '',
    resumo: '',
    destaque: false,
    imagem_url: '',
    status: 'draft',
    data_agendamento: ''
  })

  useEffect(() => {
    carregarNoticias()
  }, [paginaAtual, filtros])

  const carregarNoticias = async () => {
    setLoading(true)
    try {
      const { data, error } = await noticiasService.listarNoticias(filtros, paginaAtual, 10)

      if (error) {
        setError(error)
      } else if (data) {
        setNoticias(data.noticias)
        setTotalPaginas(data.total_paginas)
        setTotal(data.total)
      }
    } catch (err) {
      setError('Erro ao carregar notícias')
    } finally {
      setLoading(false)
    }
  }



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!user?.id) {
      setError('Usuário não autenticado')
      return
    }

    try {
      if (editingNoticia) {
        const { error } = await noticiasService.atualizarNoticia(editingNoticia.id, formData)
        if (error) {
          setError(error)
        } else {
          setSuccess('Notícia atualizada com sucesso!')
          resetForm()
          carregarNoticias()
        }
      } else {
        const { error } = await noticiasService.criarNoticia(formData, user.id)
        if (error) {
          setError(error)
        } else {
          setSuccess('Notícia criada com sucesso!')
          resetForm()
          carregarNoticias()
        }
      }
    } catch (err) {
      setError('Erro interno do servidor')
    }
  }

  const handleEdit = (noticia: Noticia) => {
    setEditingNoticia(noticia)
    // Convert scheduling date if needed
    // If status is scheduled, data_publicacao is the scheduled date.
    // data-local input needs YYYY-MM-DDTHH:mm
    const schedDate = noticia.status === 'scheduled' || noticia.status === 'published'
      ? new Date(noticia.data_publicacao).toISOString().slice(0, 16)
      : ''

    setFormData({
      titulo: noticia.titulo,
      conteudo: noticia.conteudo,
      resumo: noticia.resumo || '',
      destaque: noticia.destaque,
      imagem_url: noticia.imagem_url || '',
      status: noticia.status,
      data_agendamento: schedDate
    })
    setShowForm(true)
  }

  // State for delete confirmation
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const handleDelete = (id: string) => {
    setDeleteId(id)
  }

  const confirmDelete = async () => {
    if (!deleteId) return

    try {
      const { error } = await noticiasService.excluirNoticia(deleteId)
      if (error) {
        setError(error)
      } else {
        setSuccess('Notícia excluída com sucesso!')
        carregarNoticias()
      }
    } catch (err) {
      setError('Erro ao excluir notícia')
    } finally {
      setDeleteId(null)
    }
  }

  const handleToggleDestaque = async (id: string) => {
    try {
      const { error } = await noticiasService.alternarDestaque(id)
      if (error) {
        setError(error)
      } else {
        carregarNoticias()
      }
    } catch (err) {
      setError('Erro ao alterar destaque')
    }
  }

  const resetForm = () => {
    setFormData({
      titulo: '',
      conteudo: '',
      resumo: '',
      destaque: false,
      imagem_url: '',
      status: 'draft',
      data_agendamento: ''
    })
    setEditingNoticia(null)
    setShowForm(false)
  }

  const handleFiltroChange = (key: keyof NoticiaFiltros, value: any) => {
    setFiltros(prev => ({ ...prev, [key]: value }))
    setPaginaAtual(1)
  }

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciar Notícias</h1>
          <p className="text-gray-600 mt-1">Cadastre, edite e gerencie as notícias do sistema</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nova Notícia
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar
            </label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Título ou conteúdo..."
                value={filtros.busca || ''}
                onChange={(e) => handleFiltroChange('busca', e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Destaque
            </label>
            <select
              value={filtros.destaque === undefined ? '' : filtros.destaque.toString()}
              onChange={(e) => handleFiltroChange('destaque', e.target.value === '' ? undefined : e.target.value === 'true')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Todos</option>
              <option value="true">Em destaque</option>
              <option value="false">Sem destaque</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filtros.status || 'all'}
              onChange={(e) => handleFiltroChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Todos</option>
              <option value="published">Publicados</option>
              <option value="draft">Rascunhos</option>
              <option value="scheduled">Agendados</option>
            </select>
          </div>
        </div>
      </div>

      {/* Mensagens */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {success}
        </div>
      )}

      {/* Formulário */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              {editingNoticia ? 'Editar Notícia' : 'Nova Notícia'}
            </h2>
            <button
              onClick={resetForm}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título *
                </label>
                <input
                  type="text"
                  required
                  value={formData.titulo}
                  onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="draft">Rascunho</option>
                  <option value="published">Publicado</option>
                  <option value="scheduled">Agendado</option>
                </select>
              </div>

              {(formData.status === 'scheduled' || formData.status === 'published') && (
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.status === 'scheduled' ? 'Data de Agendamento' : 'Data de Publicação'}
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.data_agendamento}
                    onChange={(e) => setFormData(prev => ({ ...prev, data_agendamento: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required={formData.status === 'scheduled'}
                  />
                </div>
              )}



              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Resumo
                </label>
                <textarea
                  value={formData.resumo}
                  onChange={(e) => setFormData(prev => ({ ...prev, resumo: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Breve descrição da notícia..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conteúdo *
                </label>
                <div className="h-64 mb-12">
                  <ReactQuill
                    theme="snow"
                    value={formData.conteudo}
                    onChange={(content) => setFormData(prev => ({ ...prev, conteudo: content }))}
                    className="h-full"
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, false] }],
                        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        ['link', 'image'],
                        ['clean']
                      ],
                    }}
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Imagem
                </label>
                <div className="space-y-4">
                  {/* Preview */}
                  {formData.imagem_url && (
                    <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                      <img
                        src={formData.imagem_url}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, imagem_url: '' }))}
                        className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 shadow-sm"
                        title="Remover imagem"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    </div>
                  )}

                  {/* Upload Button */}
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <input
                        type="file"
                        id="image-upload"
                        className="hidden"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            const { url, error } = await noticiasService.uploadImagem(file)
                            if (url) {
                              setFormData(prev => ({ ...prev, imagem_url: url }))
                            } else {
                              alert('Erro ao fazer upload da imagem: ' + error)
                            }
                          }
                        }}
                      />
                      <label
                        htmlFor="image-upload"
                        className="cursor-pointer flex items-center gap-2 bg-white px-4 py-2 border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 text-sm font-medium text-gray-700"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-upload"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" /></svg>
                        Fazer Upload
                      </label>
                    </div>
                    <span className="text-gray-500 text-sm">ou</span>
                    <input
                      type="url"
                      value={formData.imagem_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, imagem_url: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.destaque}
                    onChange={(e) => setFormData(prev => ({ ...prev, destaque: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Destaque</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                {editingNoticia ? 'Atualizar' : 'Criar'} Notícia
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Notícias */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Notícias ({total})</h2>
            <div className="text-sm text-gray-500">
              Página {paginaAtual} de {totalPaginas}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Carregando notícias...</p>
          </div>
        ) : noticias.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>Nenhuma notícia encontrada.</p>
          </div>
        ) : (
          <div className="divide-y">
            {noticias.map((noticia) => (
              <div key={noticia.id} className="p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-gray-900">{noticia.titulo}</h3>
                      {noticia.destaque && (
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      )}
                    </div>

                    {noticia.resumo && (
                      <p className="text-gray-600 text-sm mb-2">{noticia.resumo}</p>
                    )}

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {noticia.autor}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {noticia.status === 'draft'
                          ? `Criado em: ${formatarData(noticia.data_criacao)}`
                          : formatarData(noticia.data_publicacao)}
                      </div>
                      {noticia.status !== 'published' && (
                        <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${noticia.status === 'draft' ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-600'
                          }`}>
                          {noticia.status === 'draft' ? <FileText className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                          {noticia.status === 'draft' ? 'Rascunho' : `Agendado: ${formatarData(noticia.data_publicacao)}`}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleToggleDestaque(noticia.id)}
                      className={`p-2 rounded-lg ${noticia.destaque
                        ? 'text-yellow-600 hover:bg-yellow-50'
                        : 'text-gray-400 hover:bg-gray-50'
                        }`}
                      title={noticia.destaque ? 'Remover destaque' : 'Adicionar destaque'}
                    >
                      {noticia.destaque ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                    </button>

                    <button
                      onClick={() => handleEdit(noticia)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => handleDelete(noticia.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Paginação */}
        {totalPaginas > 1 && (
          <div className="p-4 border-t">
            <div className="flex justify-center gap-2">
              <button
                onClick={() => setPaginaAtual(prev => Math.max(1, prev - 1))}
                disabled={paginaAtual === 1}
                className="px-3 py-1 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Anterior
              </button>

              {Array.from({ length: totalPaginas }, (_, i) => i + 1).map(pagina => (
                <button
                  key={pagina}
                  onClick={() => setPaginaAtual(pagina)}
                  className={`px-3 py-1 border rounded-lg ${pagina === paginaAtual
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'hover:bg-gray-50'
                    }`}
                >
                  {pagina}
                </button>
              ))}

              <button
                onClick={() => setPaginaAtual(prev => Math.min(totalPaginas, prev + 1))}
                disabled={paginaAtual === totalPaginas}
                className="px-3 py-1 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>


      {/* Delete Confirmation Modal */}
      {
        deleteId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Excluir Notícia</h3>
              <p className="text-gray-600 mb-6">Tem certeza que deseja excluir esta notícia? Esta ação não pode ser desfeita.</p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        )
      }
    </div >
  )
}

export default NoticiasPage